<template>
    <AppHead title="Switch to Annual | 10xTravel+" />
    <div class="min-h-screen bg-slate-50 dark:bg-slate-900/40">
        <!-- Header -->
        <div
            class="max-w-4xl mx-auto px-4 md:px-6 py-4 md:py-6 flex items-center justify-between"
        >
            <Link
                href="/settings/plan-billing"
                class="flex items-center gap-1 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:text-slate-100 transition-colors"
            >
                <Icon icon="chevron-left" class="w-4 h-4" />
                Back
            </Link>
            <div class="flex items-center">
                <img src="/images/logo.svg" alt="MY10x" class="h-7" />
            </div>
            <div
                class="hidden md:flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"
            >
                <Icon icon="user" class="w-5 h-5" />
                <span>{{ userName }}</span>
            </div>
        </div>

        <!-- Main Content -->
        <div class="max-w-4xl mx-auto px-4 md:px-6 pb-10 md:pb-16">
            <h1 class="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-6">
                Switch to annual plan
            </h1>

            <div class="flex flex-col-reverse md:flex-row gap-6">
                <!-- Left: Checkout Form -->
                <div
                    class="flex-1 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 md:p-8"
                >
                    <h2 class="text-xl font-medium text-slate-800 dark:text-slate-100 mb-6">
                        Checkout
                    </h2>

                    <!-- Error Message -->
                    <div
                        v-if="errorMessage"
                        class="flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-6"
                    >
                        <svg
                            class="w-5 h-5 text-red-500 shrink-0"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                clip-rule="evenodd"
                            />
                        </svg>
                        <p class="text-sm text-red-700">{{ errorMessage }}</p>
                    </div>

                    <!-- Credit Card Selection -->
                    <p class="text-sm font-medium text-slate-600 dark:text-slate-300 mb-4">
                        Credit card
                    </p>

                    <div
                        v-if="cards.length > 0"
                        class="flex flex-col gap-3 mb-4"
                    >
                        <label
                            v-for="method in cards"
                            :key="method.id"
                            class="flex items-center gap-3 cursor-pointer"
                        >
                            <input
                                type="radio"
                                :value="method.id"
                                v-model="selectedPaymentMethod"
                                class="w-4 h-4 text-orange border-slate-300 dark:border-slate-600 focus:ring-orange"
                            />
                            <div
                                class="w-10 h-7 bg-slate-100 dark:bg-slate-700 rounded flex items-center justify-center"
                            >
                                <span class="text-xs font-bold text-slate-600 dark:text-slate-300">
                                    {{ cardBrandLabel(method.brand) }}
                                </span>
                            </div>
                            <div class="flex items-center gap-2">
                                <span
                                    class="text-sm font-medium text-slate-900 dark:text-slate-100"
                                >
                                    {{ cardBrandLabel(method.brand) }}
                                    &bull;&bull;&bull;&bull;
                                    {{ method.last4 }}
                                </span>
                                <span
                                    v-if="method.is_default"
                                    class="text-xs text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 rounded-full px-2 py-0.5"
                                >
                                    Default
                                </span>
                            </div>
                            <span class="text-xs text-slate-500 dark:text-slate-400 ml-auto">
                                Exp
                                {{
                                    String(method.exp_month).padStart(2, '0')
                                }}/{{ String(method.exp_year).slice(-2) }}
                            </span>
                        </label>
                    </div>

                    <button
                        class="flex items-center gap-2 text-sm font-medium text-slate-800 dark:text-slate-100 mb-6 hover:text-10x-orange transition-colors"
                        @click="addPaymentMethod"
                    >
                        <span class="text-lg leading-none">+</span>
                        Add payment method
                    </button>

                    <!-- Info Banner -->
                    <div
                        class="flex items-start gap-3 bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-3 mb-6"
                    >
                        <Icon
                            icon="circle-info"
                            class="w-5 h-5 text-slate-500 dark:text-slate-400 shrink-0 mt-0.5"
                        />
                        <p class="text-sm text-slate-700 dark:text-slate-200">
                            You'll receive a credit for unused days on your
                            current monthly plan. The charge shown reflects the
                            prorated amount.
                        </p>
                    </div>

                    <!-- Switch Button -->
                    <button
                        type="button"
                        class="w-full bg-orange text-white text-sm font-medium py-3 px-4 rounded shadow-sm hover:bg-orange-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        :disabled="
                            processing ||
                            !selectedPaymentMethod ||
                            previewLoading
                        "
                        @click="handleSwitch"
                    >
                        <span
                            v-if="processing"
                            class="flex items-center justify-center gap-2"
                        >
                            <div
                                class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"
                            ></div>
                            Processing...
                        </span>
                        <span v-else>Switch to annual</span>
                    </button>

                    <!-- Terms -->
                    <p
                        class="text-xs text-slate-400 text-center mt-4 leading-relaxed"
                    >
                        By confirming, you authorize 10xTravel to charge you
                        according to their terms. You can cancel anytime.
                    </p>

                    <!-- Powered by Stripe -->
                    <div
                        class="flex items-center justify-center gap-3 mt-4 text-xs text-slate-400"
                    >
                        <span>Powered by</span>
                        <span class="font-bold text-slate-500 dark:text-slate-400">stripe</span>
                        <span class="text-slate-300">|</span>
                        <a
                            href="https://stripe.com/privacy"
                            target="_blank"
                            class="hover:text-slate-500 dark:text-slate-400"
                        >
                            Privacy
                        </a>
                        <span class="text-slate-300">|</span>
                        <a
                            href="https://stripe.com/legal/end-users"
                            target="_blank"
                            class="hover:text-slate-500 dark:text-slate-400"
                        >
                            Terms
                        </a>
                    </div>
                </div>

                <!-- Right: Order Summary -->
                <div
                    class="w-full md:w-80 lg:w-96 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 md:p-8 h-fit"
                >
                    <h2 class="text-xl font-medium text-slate-800 dark:text-slate-100 mb-6">
                        Order summary
                    </h2>

                    <!-- Loading state -->
                    <div
                        v-if="previewLoading"
                        class="flex items-center justify-center py-12"
                    >
                        <div
                            class="animate-spin rounded-full h-8 w-8 border-b-2 border-orange"
                        ></div>
                    </div>

                    <!-- Error state -->
                    <div v-else-if="previewError" class="py-4">
                        <p class="text-sm text-red-600">
                            {{ previewError }}
                        </p>
                    </div>

                    <template v-else-if="preview">
                        <!-- Billing Toggle (Yearly active, Monthly disabled) -->
                        <div class="bg-slate-100 dark:bg-slate-700 rounded p-0.5 flex mb-6">
                            <div
                                class="flex-1 text-sm font-medium py-2 px-4 rounded text-slate-400 flex items-center justify-center gap-1.5 relative group cursor-not-allowed"
                            >
                                Monthly
                                <!-- Tooltip -->
                                <div
                                    class="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-slate-800 text-white text-xs rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                                >
                                    You're already on monthly plan
                                </div>
                            </div>
                            <div
                                class="flex-1 text-sm font-medium py-2 px-4 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 shadow-sm border border-slate-200 dark:border-slate-700 flex items-center justify-center gap-1.5"
                            >
                                Yearly
                                <span class="text-orange text-sm font-medium">
                                    2 Months Free
                                </span>
                            </div>
                        </div>

                        <!-- Membership Info -->
                        <div class="flex items-start justify-between mb-1">
                            <div>
                                <p class="text-base font-medium text-slate-800 dark:text-slate-100">
                                    10xTravel+ membership
                                </p>
                                <p class="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                                    ${{ perMonthPrice }}/month, billed yearly
                                </p>
                            </div>
                            <p class="text-base font-medium text-slate-800 dark:text-slate-100">
                                ${{ formattedAnnualPrice }}
                            </p>
                        </div>

                        <!-- Credit for unused days -->
                        <div
                            v-if="preview.credit < 0"
                            class="flex items-center justify-between mt-4"
                        >
                            <p class="text-sm text-slate-500 dark:text-slate-400">
                                Credit for unused days
                            </p>
                            <p class="text-sm text-slate-800 dark:text-slate-100">
                                -${{ formattedCredit }}
                            </p>
                        </div>

                        <div class="h-px bg-slate-200 dark:bg-slate-700 mt-4"></div>

                        <!-- Tax -->
                        <div class="flex items-center justify-between mt-6">
                            <p class="text-sm text-slate-500 dark:text-slate-400">Tax</p>
                            <p class="text-sm text-slate-800 dark:text-slate-100">
                                ${{ formattedTax }}
                            </p>
                        </div>

                        <!-- Total -->
                        <div
                            class="flex items-center justify-between mt-4 mb-6"
                        >
                            <p class="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                Total due
                            </p>
                            <p class="text-base font-semibold text-slate-800 dark:text-slate-100">
                                ${{ formattedTotal }}
                            </p>
                        </div>

                        <div class="h-px bg-slate-200 dark:bg-slate-700"></div>

                        <!-- Benefits -->
                        <div class="flex flex-col gap-2 mt-6">
                            <div class="flex items-center gap-3">
                                <svg
                                    class="w-5 h-5 text-orange shrink-0"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="1.5"
                                >
                                    <path
                                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-.722-.064-1.43-.182-2.016z"
                                    />
                                </svg>
                                <p class="text-sm text-slate-500 dark:text-slate-400">
                                    30-day money back guarantee
                                </p>
                            </div>
                            <div class="flex items-center gap-3">
                                <svg
                                    class="w-5 h-5 text-orange shrink-0"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="1.5"
                                >
                                    <path
                                        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                                    />
                                </svg>
                                <p class="text-sm text-slate-500 dark:text-slate-400">
                                    Instant access to premium features
                                </p>
                            </div>
                            <div class="flex items-center gap-3">
                                <svg
                                    class="w-5 h-5 text-orange shrink-0"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="1.5"
                                >
                                    <path
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                </svg>
                                <p class="text-sm text-slate-500 dark:text-slate-400">
                                    Safe &amp; Secure checkout
                                </p>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Link, usePage, router } from '@inertiajs/vue3';
import AppHead from '@/Partials/AppHead.vue';
import Icon from '@/components/ui/icon/icon.vue';
import Plain from '@/Layouts/Plain.vue';
import { useTravelPlusStore } from '@/store/travelplus';

defineOptions({ layout: Plain });

const props = defineProps({
    annualProduct: {
        type: Object,
        default: () => ({}),
    },
    paymentMethods: {
        type: Array,
        default: () => [],
    },
    defaultPaymentMethodId: {
        type: String,
        default: null,
    },
    stripeKey: {
        type: String,
        default: '',
    },
});

const page = usePage();
const travelPlusStore = useTravelPlusStore();
const userName = computed(() => page.props.userData?.name || '');

const cards = ref(props.paymentMethods);
const selectedPaymentMethod = ref(
    props.defaultPaymentMethodId || props.paymentMethods[0]?.id || null
);
const processing = ref(false);
const errorMessage = ref('');

// Preview data from the API
const preview = ref(null);
const previewLoading = ref(true);
const previewError = ref('');

const formattedAnnualPrice = computed(() => {
    if (!preview.value) return '0.00';
    return Number(preview.value.annual_price).toFixed(2);
});

const perMonthPrice = computed(() => {
    if (!preview.value) return '0.00';
    return (preview.value.annual_price / 12).toFixed(2);
});

const formattedCredit = computed(() => {
    if (!preview.value) return '0.00';
    return Math.abs(preview.value.credit / 100).toFixed(2);
});

const formattedTax = computed(() => {
    if (!preview.value) return '0.00';
    return ((preview.value.tax ?? 0) / 100).toFixed(2);
});

const formattedTotal = computed(() => {
    if (!preview.value) return '0.00';
    return (preview.value.total / 100).toFixed(2);
});

const cardBrandLabel = (brand) => {
    if (!brand) return 'Card';
    const labels = {
        visa: 'Visa',
        mastercard: 'Mastercard',
        amex: 'Amex',
        discover: 'Discover',
        diners: 'Diners',
        jcb: 'JCB',
        unionpay: 'UnionPay',
    };
    return (
        labels[brand.toLowerCase()] ||
        brand.charAt(0).toUpperCase() + brand.slice(1)
    );
};

const addPaymentMethod = () => {
    router.visit('/settings/plan-billing');
};

const loadPreview = async () => {
    previewLoading.value = true;
    previewError.value = '';
    preview.value = null;

    try {
        const data = await travelPlusStore.previewUpgradeToAnnual();
        preview.value = data;
    } catch (e) {
        previewError.value =
            e?.response?.data?.message ||
            'Failed to load upgrade preview. Please try again.';
    } finally {
        previewLoading.value = false;
    }
};

const handleSwitch = async () => {
    if (processing.value || !selectedPaymentMethod.value) return;

    processing.value = true;
    errorMessage.value = '';

    try {
        // Update default payment method if different
        if (selectedPaymentMethod.value !== props.defaultPaymentMethodId) {
            await axios.put(
                `/api/billing/payment-methods/${selectedPaymentMethod.value}/default`
            );
        }

        // Execute the upgrade
        await travelPlusStore.upgradeToAnnual();

        router.visit('/settings/plan-billing');
    } catch (e) {
        errorMessage.value =
            e?.response?.data?.message ||
            'Something went wrong. Please try again.';
    } finally {
        processing.value = false;
    }
};

onMounted(() => {
    loadPreview();
});
</script>
